import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataProtection, Header } from '@aptsi-types';
import { DataAssetMappingService } from '../../shared/services/data-asset-mapping.service';

type MappingTab = 'securityZones' | 'systemAssets';
type MappingRecord = DataProtection & Record<string, unknown>;
type MappingViewKey = MappingTab | 'securityZoneSystemAssets';

interface ViewMappingDialogData {
  dataAssetId: string;
  dataAssetName?: string;
}

interface TableActionEvent {
  action?: string;
  rowIndex?: number;
  rowData?: DataProtection;
  data?: DataProtection;
}

interface PaginationMetadata {
  totalDataAssets?: number;
  totalSystemAssets?: number;
  totalSecurityZones?: number;
  totalPages?: number;
  currentPage?: number;
}

interface NormalizedResponse {
  rows: DataProtection[];
  metadata?: PaginationMetadata;
}

interface MappingTabState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  isLoading: boolean;
  isSearching: boolean;
  loadError: boolean;
  searchError: boolean;
  isSearch: boolean;
  searchText: string;
  filterByHeader: string;
  rows: DataProtection[];
  searchedRows: DataProtection[];
  loaded: boolean;
}

function createTabState(): MappingTabState {
  return {
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    isLoading: false,
    isSearching: false,
    loadError: false,
    searchError: false,
    isSearch: false,
    searchText: '',
    filterByHeader: 'all',
    rows: [],
    searchedRows: [],
    loaded: false,
  };
}

@Component({
  selector: 'app-view-mapping',
  templateUrl: './view-mapping.component.html',
  styleUrls: ['./view-mapping.component.scss'],
})
export class ViewMappingComponent implements OnInit {
  readonly itemsPerPage = 10;

  activeTab: MappingTab = 'securityZones';
  isViewingZoneAssets = false;
  selectedSecurityZone: DataProtection | null = null;

  readonly securityZonesState = createTabState();
  readonly systemAssetsState = createTabState();
  readonly securityZoneAssetsState = createTabState();

  /** Only the Security Zones table exposes the View action. */
  readonly securityZoneActionsConfig = {
    map: false,
    update: false,
    view: true,
    delete: false,
    import: false,
    configure: false,
    submit: false,
    showAssets: false,
  };

  readonly noActionsConfig = {
    map: false,
    update: false,
    view: false,
    delete: false,
    import: false,
    configure: false,
    submit: false,
    showAssets: false,
  };

  readonly securityZoneHeaders: Header[] = [
    {
      name: 'Security Zone ID',
      property: 'securityZoneId',
      showInDropdown: true,
      description: 'Unique identifier of the security zone',
    },
    {
      name: 'Security Zone Name',
      property: 'securityZoneName',
      showInDropdown: true,
      description: 'Name of the security zone',
    },
    {
      name: 'Description',
      property: 'securityZoneDescription',
      showInDropdown: true,
      description: 'Description of the security zone',
    },
    {
      name: 'Zone Type',
      property: 'securityZoneType',
      showInDropdown: true,
      description: 'Type of the security zone',
    },
  ];

  readonly systemAssetHeaders: Header[] = [
    {
      name: 'System Asset ID',
      property: 'systemAssetId',
      showInDropdown: true,
      description: 'Unique identifier of the system asset',
    },
    {
      name: 'System Asset Name',
      property: 'systemAssetName',
      showInDropdown: true,
      description: 'Name of the system asset',
    },
    {
      name: 'Description',
      property: 'systemAssetDescription',
      showInDropdown: true,
      description: 'Description of the system asset',
    },
  ];

  private cachedRows: DataProtection[] | null = null;
  private cachedViewKey: MappingViewKey | null = null;
  private cachedTableData: DataProtection[] = [];

  /** Prevent older requests from overwriting newer page/search/tab results. */
  private readonly requestVersions = new WeakMap<MappingTabState, number>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly dialogData: ViewMappingDialogData,
    private readonly dialogRef: MatDialogRef<ViewMappingComponent>,
    private readonly dataAssetMappingService: DataAssetMappingService,
    private readonly changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    void this.fetchCurrentTab();
  }

  get state(): MappingTabState {
    if (this.isViewingZoneAssets) return this.securityZoneAssetsState;
    return this.activeTab === 'securityZones' ? this.securityZonesState : this.systemAssetsState;
  }

  get headers(): Header[] {
    if (this.isViewingZoneAssets) return this.systemAssetHeaders;
    return this.activeTab === 'securityZones' ? this.securityZoneHeaders : this.systemAssetHeaders;
  }

  get tableButtonConfig(): Record<string, boolean> {
    return !this.isViewingZoneAssets && this.activeTab === 'securityZones'
      ? this.securityZoneActionsConfig
      : this.noActionsConfig;
  }

  get displayedData(): DataProtection[] {
    return this.state.isSearch ? this.state.searchedRows : this.state.rows;
  }

  get panelTitle(): string {
    if (this.isViewingZoneAssets) return 'System Assets in Security Zone';
    return this.activeTab === 'securityZones' ? 'Mapped Security Zones' : 'Mapped System Assets';
  }

  get panelDescription(): string {
    if (this.isViewingZoneAssets) {
      return `System assets associated with ${this.selectedSecurityZone?.securityZoneName || 'the selected security zone'}.`;
    }
    return this.activeTab === 'securityZones'
      ? 'Security zones associated with this data asset.'
      : 'System assets associated with this data asset.';
  }

  get recordLabel(): string {
    return this.isViewingZoneAssets || this.activeTab === 'systemAssets' ? 'system assets' : 'zones';
  }

  get searchPlaceholder(): string {
    return this.isViewingZoneAssets || this.activeTab === 'systemAssets'
      ? 'Search system assets'
      : 'Search security zones';
  }

  get noResultsDescription(): string {
    if (this.state.isSearch) return 'Try a different search term or select another category.';
    if (this.isViewingZoneAssets) return 'No system assets are associated with this security zone.';
    return this.activeTab === 'securityZones'
      ? 'No security zones are associated with this data asset.'
      : 'No system assets are associated with this data asset.';
  }

  get noResultsIcon(): string {
    return this.isViewingZoneAssets || this.activeTab === 'systemAssets' ? 'bi-hdd-network' : 'bi-shield';
  }

  /** Supplies display-friendly values while keeping the table input reference stable. */
  get tableData(): DataProtection[] {
    const source = this.displayedData;
    const viewKey = this.currentViewKey;
    if (source === this.cachedRows && viewKey === this.cachedViewKey) {
      return this.cachedTableData;
    }

    this.cachedTableData = source.map((item) => {
      const row: Record<string, unknown> = { ...(item as MappingRecord) };
      for (const header of this.headers) {
        const value = row[header.property];
        row[header.property] =
          value === null || value === undefined || String(value).trim() === '' ? '—' : value;
      }
      return row as unknown as DataProtection;
    });

    this.cachedRows = source;
    this.cachedViewKey = viewKey;
    return this.cachedTableData;
  }

  selectTab(tab: MappingTab): void {
    if (this.isViewingZoneAssets || this.activeTab === tab) return;
    this.activeTab = tab;
    this.invalidateTableCache();

    const nextState = this.state;
    if (!nextState.loaded) void this.fetchCurrentTab();
  }

  /** Open the system assets belonging to the selected security zone. */
  handleTableAction(event: TableActionEvent): void {
    if (this.isViewingZoneAssets || this.activeTab !== 'securityZones') return;
    if (String(event?.action ?? '').trim().toLowerCase() !== 'view') return;

    const zone = event.rowData ?? event.data ?? this.displayedData[event.rowIndex ?? -1];
    const securityZoneId = String(zone?.securityZoneId ?? '').trim();
    if (!zone || !securityZoneId || securityZoneId === '—') {
      console.error('[ViewMapping] Cannot open security zone assets: securityZoneId is missing.', event);
      return;
    }

    this.selectedSecurityZone = zone;
    this.isViewingZoneAssets = true;
    Object.assign(this.securityZoneAssetsState, createTabState());
    this.invalidateTableCache();
    void this.loadRows(false);
  }

  /** Return to the tabs without resetting either tab's search/page state. */
  backToMappingTabs(): void {
    this.isViewingZoneAssets = false;
    this.invalidateTableCache();
  }

  async fetchCurrentTab(): Promise<void> {
    await this.loadRows(false);
  }

  async searchCurrentTab(): Promise<void> {
    const query = this.state.searchText.trim();
    if (!query) {
      await this.clearSearch();
      return;
    }

    this.state.searchText = query;
    this.state.isSearch = true;
    this.state.currentPage = 1;
    await this.loadRows(true);
  }

  async fetchCurrentTabSearch(): Promise<void> {
    if (!this.state.searchText.trim()) {
      await this.clearSearch();
      return;
    }
    await this.loadRows(true);
  }

  async clearSearch(): Promise<void> {
    const state = this.state;
    state.searchText = '';
    state.isSearch = false;
    state.searchError = false;
    state.currentPage = 1;
    state.searchedRows = [];
    this.invalidateTableCache();
    await this.loadRows(false);
  }

  onFilterChange(): void {
    this.state.currentPage = 1;
    if (this.state.searchText.trim()) {
      this.state.isSearch = true;
      void this.loadRows(true);
    } else {
      this.state.isSearch = false;
      void this.loadRows(false);
    }
  }

  onInputChange(event: Event): void {
    this.state.searchText = (event.target as HTMLInputElement).value;
  }

  changePage(page: number): void {
    const state = this.state;
    if (
      !Number.isInteger(page) ||
      page < 1 ||
      page > state.totalPages ||
      page === state.currentPage
    ) {
      return;
    }

    state.currentPage = page;
    void this.loadRows(state.isSearch);
  }

  retryLoad(): void {
    void this.loadRows(false);
  }

  close(): void {
    this.dialogRef.close();
  }

  private get currentViewKey(): MappingViewKey {
    return this.isViewingZoneAssets ? 'securityZoneSystemAssets' : this.activeTab;
  }

  private async loadRows(search: boolean): Promise<void> {
    const isZoneAssetsView = this.isViewingZoneAssets;
    const tab = this.activeTab;
    const state = this.state;
    const dataAssetId = String(this.dialogData?.dataAssetId ?? '').trim();
    const securityZoneId = String(this.selectedSecurityZone?.securityZoneId ?? '').trim();

    if (isZoneAssetsView ? !securityZoneId || securityZoneId === '—' : !dataAssetId) {
      state.loadError = true;
      state.isLoading = false;
      state.isSearching = false;
      this.refreshView();
      return;
    }

    // Keep a monotonically increasing request version for each independent list.
    // An older response for the same list must not overwrite newer search/page data.
    const requestVersion = (this.requestVersions.get(state) ?? 0) + 1;
    this.requestVersions.set(state, requestVersion);

    if (search) {
      state.isSearching = true;
      state.searchError = false;
    } else {
      state.isLoading = true;
      state.loadError = false;
    }

    try {
      const pageIndex = state.currentPage - 1; // UI pages are 1-based; API indexes are 0-based.
      let rawResponse: unknown;

      if (isZoneAssetsView) {
        rawResponse = search
          ? await this.dataAssetMappingService.FetchSystemAssetsBySecurityZoneIdSearch(
              pageIndex,
              state.searchText.trim(),
              state.filterByHeader,
              securityZoneId,
            ).toPromise()
          : await this.dataAssetMappingService.FetchSystemAssetsBySecurityZoneId(
              pageIndex,
              securityZoneId,
            ).toPromise();
      } else if (tab === 'securityZones') {
        rawResponse = search
          ? await this.dataAssetMappingService.FetchMappedSecurityZonesByDataAssetIdSearch(
              pageIndex,
              state.searchText.trim(),
              state.filterByHeader,
              dataAssetId,
            ).toPromise()
          : await this.dataAssetMappingService.FetchMappedSecurityZonesByDataAssetId(
              pageIndex,
              dataAssetId,
            ).toPromise();
      } else {
        rawResponse = search
          ? await this.dataAssetMappingService.FetchMappedSystemAssetsByDataAssetIdSearch(
              pageIndex,
              state.searchText.trim(),
              state.filterByHeader,
              dataAssetId,
            ).toPromise()
          : await this.dataAssetMappingService.FetchMappedSystemAssetsByDataAssetId(
              pageIndex,
              dataAssetId,
            ).toPromise();
      }

      // Ignore stale responses before touching rows, totals, or loading state.
      if (requestVersion !== this.requestVersions.get(state)) {
        return;
      }

      const normalized = this.normalizeResponse(rawResponse);
      const preferredTotal = isZoneAssetsView || tab === 'systemAssets'
        ? normalized.metadata?.totalSystemAssets
        : normalized.metadata?.totalSecurityZones;
      const totalFallback = this.readNumber(
        normalized.metadata?.totalDataAssets,
        normalized.rows.length,
      );
      const totalItems = this.readNumber(preferredTotal, totalFallback);
      // Backend pagination metadata is authoritative. Do not derive this from row counts.
      const totalPages = this.readNumber(normalized.metadata?.totalPages, 0);

      // If the number of pages shrank while the user was viewing a later page,
      // request the last valid page rather than displaying a stale/empty page.
      if (totalPages > 0 && state.currentPage > totalPages) {
        state.currentPage = totalPages;
        await this.loadRows(search);
        return;
      }

      if (totalPages === 0 && state.currentPage !== 1) {
        state.currentPage = 1;
        await this.loadRows(search);
        return;
      }
      if (totalPages === 0) {
        state.currentPage = 1;
      }

      if (search) {
        state.searchedRows = normalized.rows;
        state.searchError = false;
      } else {
        state.rows = normalized.rows;
        state.loadError = false;
        state.loaded = true;
      }

      // Preserve zero when the backend explicitly reports an empty result set.
      state.totalItems = totalItems;
      state.totalPages = totalPages;
      this.invalidateTableCache();
    } catch (error) {
      if (requestVersion !== this.requestVersions.get(state)) {
        return;
      }

      console.error(
        `Failed to load ${isZoneAssetsView ? 'system assets for security zone' : `${tab} mapping details`}`,
        error,
      );
      if (search) {
        state.searchError = true;
        state.searchedRows = [];
        state.totalItems = 0;
        state.totalPages = 0;
      } else {
        state.loadError = true;
        state.rows = [];
        state.totalItems = 0;
        state.totalPages = 0;
      }
      this.invalidateTableCache();
    } finally {
      // A newer request owns the loading indicators if it superseded this one.
      if (requestVersion === this.requestVersions.get(state)) {
        state.isLoading = false;
        state.isSearching = false;
        this.refreshView();
      }
    }
  }

  private normalizeResponse(raw: unknown): NormalizedResponse {
    if (Array.isArray(raw)) {
      const rows = raw
        .filter((value): value is Record<string, unknown> => !!value && typeof value === 'object')
        .map((value) => value as unknown as DataProtection);
      return { rows, metadata: this.findMetadata(raw) };
    }

    if (raw && typeof raw === 'object') {
      const wrapper = raw as Record<string, unknown>;
      for (const key of ['data', 'items', 'content', 'results', 'records']) {
        if (Array.isArray(wrapper[key])) {
          const values = wrapper[key] as unknown[];
          return {
            rows: values
              .filter((value): value is Record<string, unknown> => !!value && typeof value === 'object')
              .map((value) => value as unknown as DataProtection),
            metadata: this.readMetadata(wrapper['metadata'] ?? wrapper['meta'] ?? wrapper) ?? this.findMetadata(values),
          };
        }
      }
      return {
        rows: [],
        metadata: this.readMetadata(wrapper['metadata'] ?? wrapper['meta'] ?? wrapper),
      };
    }

    return { rows: [] };
  }

  private findMetadata(values: unknown[]): PaginationMetadata | undefined {
    const value = values.find((candidate) => {
      if (!candidate || typeof candidate !== 'object') return false;
      const row = candidate as Record<string, unknown>;
      return ['totalDataAssets', 'totalSystemAssets', 'totalSecurityZones', 'totalPages', 'currentPage']
        .some((key) => row[key] !== undefined);
    });
    return value ? this.readMetadata(value) : undefined;
  }

  private readMetadata(value: unknown): PaginationMetadata | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const source = value as Record<string, unknown>;
    const result: PaginationMetadata = {};
    for (const key of ['totalDataAssets', 'totalSystemAssets', 'totalSecurityZones', 'totalPages', 'currentPage'] as const) {
      const rawValue = source[key];
      if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
        const parsed = Number(rawValue);
        if (Number.isFinite(parsed) && parsed >= 0) result[key] = parsed;
      }
    }
    return Object.keys(result).length ? result : undefined;
  }

  private readNumber(value: unknown, fallback: number): number {
    if (value === null || value === undefined || (typeof value === 'string' && !value.trim())) {
      return fallback;
    }
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
  }

  private invalidateTableCache(): void {
    this.cachedRows = null;
    this.cachedViewKey = null;
  }

  private refreshView(): void {
    this.changeDetector.detectChanges();
  }
}
